import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, MapPin, Sparkles, GraduationCap, Bot, Users, Building2, Star, Megaphone, Phone } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import AIChat from "../components/AIChat";
import BoardStrip from "../components/BoardStrip";
import EventsFlyerGrid from "../components/EventsFlyerGrid";
import { useI18n } from "../i18n/I18nContext";
import { BRAND } from "../lib/brand";
import { api } from "../lib/nailApi";

const HERO_IMG = "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=2000&q=80";

export default function HomePage() {
  const { lang } = useI18n();
  const [events, setEvents] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [founders, setFounders] = useState([]);

  useEffect(() => {
    api.get("/events/list", { params: { include_past: true } }).then((r) => setEvents(r.data.events || [])).catch(() => {});
    api.get("/speakers/list").then((r) => setSpeakers((r.data.speakers || []).slice(0, 4))).catch(() => {});
    api.get("/sponsors/list").then((r) => setSponsors((r.data.sponsors || []).slice(0, 10))).catch(() => {});
    api.get("/organizers/wall", { params: { limit: 8 } }).then((r) => setFounders(r.data.founders || [])).catch(() => {});
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcomingEvents = (events || [])
    .filter((e) => (e.event_date || "") >= today)
    .sort((a, b) => (a.event_date || "").localeCompare(b.event_date || ""));
  const pastEvents = (events || [])
    .filter((e) => (e.event_date || "") < today)
    .sort((a, b) => (b.event_date || "").localeCompare(a.event_date || ""));

  const featured = upcomingEvents[0] || null;
  const upcoming = upcomingEvents.slice(1, 6);

  return (
    <div className="min-h-screen bg-white text-charcoal" data-testid="home-page">
      <SiteHeader floating variant="dark" />

      {/* HERO */}
      <section className="relative overflow-hidden dark-surface">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-charcoal-900/60" />
          <div className="absolute inset-0 summit-gradient opacity-90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10 pt-28 sm:pt-32 md:pt-40 pb-16 sm:pb-20 md:pb-28 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold-500/40 bg-white/5 backdrop-blur-md animate-fade-up">
            <Sparkles className="w-3.5 h-3.5 text-gold-500" />
            <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.24em] sm:tracking-[0.28em] text-gold-300 font-semibold">
              {lang === "en" ? "The business community for Vietnamese nail professionals" : "Cộng đồng kinh doanh dành cho người làm nail Việt"}
            </span>
          </div>
          <h1 className="mt-5 sm:mt-6 font-serif text-[2.25rem] leading-[1.05] sm:text-5xl md:text-6xl lg:text-7xl sm:leading-[1.02] tracking-tight max-w-4xl animate-fade-up">
            {lang === "en" ? (
              <>Welcome to <span className="italic text-gold-500">Nail Viet Club</span></>
            ) : (
              <>Chào mừng đến với <span className="italic text-gold-500">Nail Viet Club</span></>
            )}
          </h1>
          <p className="mt-5 sm:mt-6 text-base sm:text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed animate-fade-up [animation-delay:120ms]">
            {lang === "en" ? BRAND.taglineEn : BRAND.taglineVi} — {lang === "en"
              ? "events, education, AI guidance, business resources, and networking for nail salon owners across the U.S."
              : "sự kiện, đào tạo, AI, tài nguyên kinh doanh và networking dành cho chủ tiệm nail trên toàn nước Mỹ."}
          </p>
          <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3 animate-fade-up [animation-delay:200ms]">
            <Link to="/events" data-testid="home-cta-events" className="inline-flex items-center justify-center gap-2 bg-emerald2 text-white rounded-full px-6 sm:px-7 py-3.5 font-medium hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-12px_rgba(14,91,79,0.6)] transition-all">
              {lang === "en" ? "Find events" : "Tìm sự kiện"} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/organizer/signup" data-testid="home-cta-host" className="inline-flex items-center justify-center gap-2 bg-charcoal text-white rounded-full px-6 sm:px-7 py-3.5 font-medium hover:-translate-y-0.5 hover:bg-charcoal-900 transition-all">
              <Megaphone className="w-4 h-4" /> {lang === "en" ? "Host an event" : "Tổ chức sự kiện"}
            </Link>
            <Link to="/ai" data-testid="home-cta-ai" className="inline-flex items-center justify-center gap-2 bg-gold-500 text-charcoal rounded-full px-6 sm:px-7 py-3.5 font-medium hover:-translate-y-0.5 hover:bg-gold-400 transition-all">
              <Bot className="w-4 h-4" /> {lang === "en" ? "Ask the AI Advisor" : "Hỏi Cố Vấn AI"}
            </Link>
            <a
              href={`tel:${BRAND.phone}`}
              data-testid="home-cta-call"
              className="inline-flex items-center justify-center gap-2 border border-gold-500/60 bg-white/5 text-white backdrop-blur-md rounded-full px-6 sm:px-7 py-3.5 font-medium hover:bg-white/10 transition-all"
            >
              <Phone className="w-4 h-4 text-gold-300" />
              <span className="flex flex-col items-start leading-tight">
                <span className="text-[10px] uppercase tracking-[0.22em] text-gold-300 font-semibold">
                  {lang === "en" ? "Call AI Voice 24/7" : "Gọi Cố Vấn AI 24/7"}
                </span>
                <span className="font-mono text-sm sm:text-base">{BRAND.phoneDisplay}</span>
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Phase 8.11 — EVENTS FLYER GRID (mobile-first premium grid + modal).
          Shows upcoming + past in one band, sorted dynamically by event_date. */}
      <EventsFlyerGrid events={events} />

      {/* FEATURED EVENT (only rendered when no upcoming events exist as a fallback) */}
      {featured && upcomingEvents.length === 0 && (
        <section className="bg-warmgray-50">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 py-14 sm:py-20 md:py-24">
            <p className="text-[11px] uppercase tracking-[0.32em] text-emerald2 font-semibold mb-4 sm:mb-5">{lang === "en" ? "Featured event" : "Sự kiện nổi bật"}</p>
            <div className="grid md:grid-cols-12 gap-6 sm:gap-8 items-stretch">
              <div className="md:col-span-7 rounded-3xl overflow-hidden bg-charcoal-900 relative min-h-[280px] sm:min-h-[320px]" data-testid="featured-event-card">
                <img src={featured.hero_image_url || HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
                <div className="absolute inset-0 summit-gradient opacity-80" />
                <div className="relative p-6 sm:p-8 md:p-12 text-white h-full flex flex-col justify-end">
                  <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-gold-300 font-semibold"><Calendar className="w-3 h-3" />{featured.event_date} · {featured.event_time}</div>
                  <h3 className="mt-2 font-serif text-2xl sm:text-3xl md:text-5xl leading-tight">{featured.name}</h3>
                  {featured.subtitle && <p className="mt-2 text-gold-300 font-medium">{featured.subtitle}</p>}
                  <div className="mt-4 inline-flex items-center gap-2 text-white/80 text-sm"><MapPin className="w-4 h-4" />{featured.venue_name || featured.location}</div>
                  <div className="mt-6">
                    <Link to={`/events/${featured.slug}`} data-testid="featured-event-link" className="inline-flex items-center gap-2 bg-gold-500 text-charcoal rounded-full px-5 py-2.5 font-medium hover:bg-gold-400 transition-all">
                      {lang === "en" ? "View & register" : "Xem & Đăng ký"} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
              <div className="md:col-span-5 grid grid-cols-1 gap-4">
                <WhyJoin lang={lang} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* WHY JOIN (when no featured) */}
      {!featured && (
        <section className="bg-warmgray-50">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
            <p className="text-[11px] uppercase tracking-[0.32em] text-emerald2 font-semibold mb-5">{lang === "en" ? "Why join" : "Vì sao tham gia"}</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal">{lang === "en" ? "Built for nail salon owners" : "Dành cho chủ tiệm nail"}</h2>
            <div className="mt-10"><WhyJoin lang={lang} /></div>
          </div>
        </section>
      )}

      {/* AI ADVISOR PROMO */}
      <section className="bg-charcoal-900 dark-surface relative overflow-hidden">
        <div className="absolute inset-0 summit-gradient opacity-90" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10 items-center text-white">
          <div className="md:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.32em] text-gold-300 font-semibold mb-5">{lang === "en" ? "Powered by AsterPOS Corp" : "Được cung cấp bởi AsterPOS Corp"}</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight">AsterPOS AI Business Advisor</h2>
            <p className="mt-6 text-white/80 text-lg leading-relaxed max-w-2xl">
              {lang === "en"
                ? "An AI advisor purpose-built for nail salon owners. Educational guidance on business, taxes, legal, HR, marketing, compliance and the AsterPOS platform — available 24/7 in Vietnamese and English."
                : "Trợ lý AI dành riêng cho chủ tiệm nail. Hướng dẫn về kinh doanh, thuế, pháp lý, nhân sự, marketing, tuân thủ và nền tảng AsterPOS — 24/7, Tiếng Việt và Tiếng Anh."}
            </p>
            <Link to="/ai" data-testid="home-ai-cta" className="mt-8 inline-flex items-center gap-2 bg-gold-500 text-charcoal rounded-full px-6 py-3 font-semibold hover:bg-gold-400 hover:-translate-y-0.5 transition-all">
              <Bot className="w-4 h-4" /> {lang === "en" ? "Open the AI Advisor" : "Mở Cố Vấn AI"}
            </Link>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-3">
            {["Business", "Taxes", "HR", "Legal", "Marketing", "Compliance", "Operations", "AsterPOS"].map((k) => (
              <div key={k} className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-sm">{k}</div>
            ))}
          </div>
        </div>
      </section>

      {/* WALL OF FOUNDERS — homepage preview */}
      {founders.length > 0 && (
        <section className="bg-white" data-testid="home-founders-strip">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 py-16 sm:py-20">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
              <div>
                <p className="text-[11px] uppercase tracking-[0.32em] text-emerald2 font-semibold mb-3">
                  {lang === "en" ? "Wall of Founders" : "Nhà sáng lập"}
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-charcoal leading-tight">
                  {lang === "en" ? "Organizers shaping the community" : "Những nhà tổ chức kiến tạo cộng đồng"}
                </h2>
              </div>
              <Link
                to="/organizers"
                data-testid="home-founders-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-emerald2 hover:text-emerald2/80"
              >
                {lang === "en" ? "View all" : "Xem tất cả"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {founders.slice(0, 8).map((f) => (
                <Link
                  key={f.id}
                  to={f.featured_event ? `/events/${f.featured_event.slug}` : "/organizers"}
                  data-testid={`home-founder-${f.slug}`}
                  className="group rounded-2xl bg-warmgray-50 border border-warmgray-200 hover:border-emerald2/40 hover:-translate-y-0.5 transition-all p-4 sm:p-5 flex items-center gap-3"
                >
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-white border border-warmgray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {f.logo_url ? (
                      <img src={f.logo_url} alt={f.name} className="w-full h-full object-contain" />
                    ) : (
                      <Sparkles className="w-5 h-5 text-emerald2" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-charcoal truncate">{f.name}</div>
                    {(f.city || f.state) && (
                      <div className="text-[11px] text-warmgray-500 truncate inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {[f.city, f.state].filter(Boolean).join(", ")}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HOST AN EVENT — multi-channel onboarding band */}
      <section className="bg-warmgray-50" data-testid="home-host-band">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 py-16 sm:py-20">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <p className="text-[11px] uppercase tracking-[0.32em] text-emerald2 font-semibold mb-3">
                {lang === "en" ? "Host with us" : "Tổ chức cùng chúng tôi"}
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-charcoal leading-tight">
                {lang === "en" ? "Want to host a nail or beauty event?" : "Bạn muốn tổ chức sự kiện ngành nail hoặc beauty?"}
              </h2>
              <p className="mt-4 text-warmgray-700 text-base sm:text-lg leading-relaxed max-w-2xl">
                {lang === "en"
                  ? "Sign up on the web, chat with our AI advisor, or call our AI toll-free line — same backend, three easy ways to create your organizer account."
                  : "Đăng ký qua web, chat với AI hoặc gọi tổng đài AI miễn phí — cùng một hệ thống, ba cách tạo tài khoản tổ chức sự kiện."}
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/organizer/signup"
                  data-testid="home-host-signup"
                  className="inline-flex items-center justify-center gap-2 bg-emerald2 text-white rounded-full px-6 py-3 font-semibold hover:-translate-y-0.5 transition-all"
                >
                  <Megaphone className="w-4 h-4" /> {lang === "en" ? "Sign up online" : "Đăng ký trên web"}
                </Link>
                <Link
                  to="/ai"
                  data-testid="home-host-chat"
                  className="inline-flex items-center justify-center gap-2 bg-gold-500 text-charcoal rounded-full px-6 py-3 font-semibold hover:bg-gold-400 transition-all"
                >
                  <Bot className="w-4 h-4" /> {lang === "en" ? "Chat with AI to create my event" : "Chat AI để tạo sự kiện"}
                </Link>
                <Link
                  to="/contact"
                  data-testid="home-host-call"
                  className="inline-flex items-center justify-center gap-2 border border-charcoal/15 text-charcoal rounded-full px-6 py-3 font-semibold hover:bg-warmgray-100 transition-all"
                >
                  {lang === "en" ? "Call or text our AI" : "Gọi hoặc nhắn AI"}
                </Link>
              </div>
              <p className="mt-4 text-xs text-warmgray-600">
                {lang === "en"
                  ? "Already an organizer? "
                  : "Đã có tài khoản? "}
                <Link to="/organizer/login" data-testid="home-host-login" className="underline font-medium text-charcoal">
                  {lang === "en" ? "Sign in" : "Đăng nhập"}
                </Link>
              </p>
            </div>
            <div className="md:col-span-5 grid sm:grid-cols-3 gap-2 sm:gap-3">
              {[
                { k: lang === "en" ? "Web form" : "Web form" },
                { k: lang === "en" ? "AI Chat" : "AI Chat" },
                { k: lang === "en" ? "AI Voice" : "AI thoại" },
              ].map((c) => (
                <div key={c.k} className="bg-white border border-warmgray-200 rounded-2xl px-4 py-5 text-center">
                  <div className="font-serif text-2xl text-charcoal">{c.k}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-warmgray-600">
                    {lang === "en" ? "Same backend" : "Cùng hệ thống"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SPEAKERS */}
      {speakers.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <p className="text-[11px] uppercase tracking-[0.32em] text-emerald2 font-semibold mb-3">{lang === "en" ? "Featured speakers" : "Diễn giả nổi bật"}</p>
              <h2 className="font-serif text-4xl md:text-5xl text-charcoal">{lang === "en" ? "Hear from the best" : "Lắng nghe từ chuyên gia"}</h2>
            </div>
            <Link to="/speakers" data-testid="home-speakers-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald2 hover:text-emerald2-deep">
              {lang === "en" ? "Full directory" : "Tất cả diễn giả"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-5">
            {speakers.map((sp, i) => (
              <div key={i} className="bg-white border border-warmgray-200 rounded-3xl overflow-hidden hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(14,91,79,0.25)] transition-all" data-testid={`home-speaker-${i}`}>
                <div className="aspect-[4/5] bg-warmgray-100 overflow-hidden">
                  {sp.photo_url ? <img src={sp.photo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-warmgray-500 font-serif text-4xl">{sp.full_name?.[0]}</div>}
                </div>
                <div className="p-4">
                  <div className="font-serif text-xl text-charcoal flex items-center gap-1.5">{sp.full_name} {sp.is_featured && <Star className="w-3.5 h-3.5 text-gold-500" />}</div>
                  <div className="text-xs text-warmgray-700 mt-1 truncate">{[sp.title, sp.company].filter(Boolean).join(" · ")}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* BOARD OF DIRECTORS — compact strip between Speakers and Sponsors */}
      <BoardStrip />

      {/* SPONSORS */}
      {sponsors.length > 0 && (
        <section className="bg-warmgray-50">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
              <div>
                <p className="text-[11px] uppercase tracking-[0.32em] text-emerald2 font-semibold mb-3">{lang === "en" ? "Community partners" : "Đối tác cộng đồng"}</p>
                <h2 className="font-serif text-3xl md:text-4xl text-charcoal">{lang === "en" ? "Sponsors" : "Nhà tài trợ"}</h2>
              </div>
              <Link to="/sponsors" className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald2 hover:text-emerald2-deep">
                {lang === "en" ? "All sponsors" : "Tất cả tài trợ"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-5">
              {sponsors.map((sp, i) => (
                <a key={i} href={sp.website || "#"} target={sp.website ? "_blank" : "_self"} rel="noopener noreferrer"
                  className="aspect-[3/2] bg-white border border-warmgray-200 rounded-2xl flex items-center justify-center p-4 hover:-translate-y-0.5 hover:border-gold-500/40 transition-all">
                  {sp.logo_url ? <img src={sp.logo_url} alt={sp.name} className="max-h-full max-w-full object-contain opacity-80 hover:opacity-100" /> : <span className="font-serif text-lg text-charcoal text-center">{sp.name}</span>}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PAST EVENTS moved to /events page — homepage stays focused on upcoming only */}

      <SiteFooter />
      <AIChat eventSlug={featured?.slug} />
    </div>
  );
}

function WhyJoin({ lang }) {
  const items = [
    { Icon: Users, vi: "Cộng đồng chủ tiệm", en: "Owner community" },
    { Icon: GraduationCap, vi: "Học hỏi từ chuyên gia", en: "Learn from experts" },
    { Icon: Bot, vi: "Cố vấn AI 24/7", en: "24/7 AI Advisor" },
    { Icon: Building2, vi: "Đối tác & nhà cung cấp", en: "Partners & vendors" },
  ];
  return (
    <div className="grid grid-cols-2 gap-3">
      {items.map(({ Icon, vi, en }, i) => (
        <div key={i} className="bg-white border border-warmgray-200 rounded-2xl p-4">
          <div className="w-10 h-10 rounded-xl bg-emerald2 text-white flex items-center justify-center"><Icon className="w-5 h-5" /></div>
          <div className="mt-3 font-medium text-charcoal text-sm">{lang === "en" ? en : vi}</div>
        </div>
      ))}
    </div>
  );
}
